import React, { useState, useEffect, useRef, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import * as d3 from "d3";
import { PREDICTOR_NAMES, PREDICTOR_COLORS } from "./accuracyConstants";
import "./PredictorDetailPanel.css";

const DETAIL_METRICS = [
    { key: "combined",  label: "Overall",  gwDataKey: "combined_score",  ciValKey: "combined_score",  ciLoKey: "combined_score_ci_lower",  ciHiKey: "combined_score_ci_upper",  ciSource: "combined",  posValKey: "combined",      posLoKey: "combined_ci_lower",      posHiKey: "combined_ci_upper",      posOthersKey: "combined" },
    { key: "error",     label: "Error",    gwDataKey: "rmse",            ciValKey: "rmse",            ciLoKey: "rmse_ci_lower",            ciHiKey: "rmse_ci_upper",            ciSource: "overall",   posValKey: "rmse",          posLoKey: "rmse_ci_lower",          posHiKey: "rmse_ci_upper",          posOthersKey: "rmse" },
    { key: "order",     label: "Order",    gwDataKey: "c_index",         ciValKey: "c_index",         ciLoKey: "c_index_ci_lower",         ciHiKey: "c_index_ci_upper",         ciSource: "overall",   posValKey: "c_index",       posLoKey: "c_index_ci_lower",       posHiKey: "c_index_ci_upper",       posOthersKey: "c_index" },
    { key: "returns",   label: "Returns",  gwDataKey: "returns_rmse",    ciValKey: "rmse",            ciLoKey: "rmse_ci_lower",            ciHiKey: "rmse_ci_upper",            ciSource: "returns",   posValKey: "returns_rmse",  posLoKey: "returns_rmse_ci_lower",  posHiKey: "returns_rmse_ci_upper",  posOthersKey: "returns_rmse" },
];

const PredictorDetailPanel = ({ predictor, onClose, byGameweek, byPredictor, timeframe, selectedGw }) => {
    const [metric, setMetric] = useState("combined");
    const [outlierMode, setOutlierMode] = useState("players");
    const contentRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(300);
    const [closing, setClosing] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 650);

    // Track mobile/desktop
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 650);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Lock body scroll on mobile when panel is open
    useEffect(() => {
        if (isMobile && predictor && !closing) document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, [predictor, isMobile, closing]);

    // Handle close with animation on mobile
    const handleClose = () => {
        if (isMobile) {
            setClosing(true);
            setTimeout(() => {
                setClosing(false);
                onClose();
            }, 400);
        } else {
            onClose();
        }
    };

    // Responsive chart width
    useEffect(() => {
        const measure = () => {
            if (contentRef.current) setChartWidth(contentRef.current.getBoundingClientRect().width);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [predictor]);

    // Look up predictor-specific data from by_predictor
    const predictorData = byPredictor && predictor ? byPredictor[predictor] : null;
    const isGwByGw = timeframe === "gwbygw";
    // A single gameweek has no meaningful confidence interval, so CI-based stats
    // (overall/returns/combined) fall back to season-level context in that mode.
    const ciTimeframe = isGwByGw ? "season" : timeframe;
    const timeframeData = predictorData?.[ciTimeframe];
    const overall = timeframeData?.overall || [];
    const returns = timeframeData?.returns || [];
    const combined = timeframeData?.combined || [];

    const activeMetric = DETAIL_METRICS.find(m => m.key === metric) || DETAIL_METRICS[0];
    const metricDataKey = activeMetric.gwDataKey;

    // Build GW series
    const { allSeries, gameweeks } = useMemo(() => {
        if (!byGameweek) return { allSeries: [], gameweeks: [] };
        const map = {};
        Object.entries(byGameweek).forEach(([gw, entries]) => {
            const gwNum = parseInt(gw, 10);
            entries.forEach(e => {
                if (e.predictor === "benchmark") return;
                const val = e[metricDataKey];
                if (val == null) return;
                if (!map[e.predictor]) map[e.predictor] = [];
                map[e.predictor].push({ gw: gwNum, value: val });
            });
        });
        const gws = Object.keys(byGameweek).map(Number).sort((a, b) => a - b);
        const series = Object.keys(map).map(p => ({
            predictor: p,
            values: map[p].sort((a, b) => a.gw - b.gw),
        }));
        return { allSeries: series, gameweeks: gws };
    }, [byGameweek, metricDataKey]);

    // Build CI data from predictor-specific data
    const ciData = useMemo(() => {
        return DETAIL_METRICS.map(m => {
            const src = m.ciSource === "combined" ? combined
                      : m.ciSource === "returns" ? returns
                      : overall;
            const hasCI = m.ciLoKey != null;
            const entries = src
                .filter(d => d.predictor !== "benchmark" && d[m.ciValKey] != null)
                .map(d => ({
                    predictor: d.predictor,
                    value: d[m.ciValKey],
                    lo: hasCI ? d[m.ciLoKey] : null,
                    hi: hasCI ? d[m.ciHiKey] : null,
                }));
            return { key: m.key, label: m.label, entries, hasCI };
        });
    }, [overall, returns, combined]);

    if (!predictor && !closing) {
        // On mobile, fully hide; on desktop, show empty placeholder
        if (isMobile) return null;
        return (
            <div className="detail-panel detail-panel-empty">
                <div className="detail-empty-message">
                    Click a predictor to see detailed stats
                </div>
            </div>
        );
    }

    const color = PREDICTOR_COLORS[predictor] || "#2a7b9b";
    const displayName = PREDICTOR_NAMES[predictor] || predictor;

    // --- GW chart ---
    const maxChartWidth = 380;
    const effectiveChartWidth = Math.min(chartWidth, maxChartWidth);
    const margin = { top: 10, right: 16, bottom: 28, left: 36 };
    const svgH = 180;
    const w = Math.max(effectiveChartWidth - margin.left - margin.right, 10);
    const h = svgH - margin.top - margin.bottom;

    const selectedSeries = allSeries.find(s => s.predictor === predictor);
    const competitors   = allSeries.filter(s => s.predictor !== predictor);
    const allVals = allSeries.flatMap(s => s.values.map(v => v.value));
    const yMin = Math.min(0, d3.min(allVals) ?? 0) - 1;
    const yMax = (d3.max(allVals) ?? 10) + 1;
    const xScale = d3.scaleLinear().domain([d3.min(gameweeks) ?? 1, d3.max(gameweeks) ?? 1]).range([0, w]);
    const yScale = d3.scaleLinear().domain([yMin, yMax]).range([h, 0]);
    const lineFn = d3.line().x(d => xScale(d.gw)).y(d => yScale(d.value));
    const xTicks = xScale.ticks(Math.min(gameweeks.length, 7)).filter(Number.isInteger);
    const yTicks = yScale.ticks(5);

    // --- CI chart (shared x-axis) ---
    const ciLabelW = 96;
    const ciPlotW = Math.max(effectiveChartWidth - ciLabelW - 24, 10);

    // Compute shared domain across all CI categories, always including 0 (benchmark)
    const allCIValsGlobal = ciData.flatMap(ci => ci.entries.flatMap(e =>
        e.lo != null ? [e.lo, e.hi] : [e.value]
    ));
    const ciGlobalMin = Math.min(0, d3.min(allCIValsGlobal) ?? 0) - 1;
    const ciGlobalMax = Math.max(0, d3.max(allCIValsGlobal) ?? 0) + 1;
    const ciScale = d3.scaleLinear().domain([ciGlobalMin, ciGlobalMax]).range([0, ciPlotW]);

    const renderCI = (ci) => {
        if (!ci.entries.length) return <div className="detail-ci-empty">No data</div>;
        const selected = ci.entries.find(e => e.predictor === predictor);
        const ciCompetitors = ci.entries.filter(e => e.predictor !== predictor);
        const rowH = 24;
        const totalRows = (selected ? 1 : 0) + 1;
        const svgHeight = totalRows * rowH + 8;
        return (
            <svg width={ciLabelW + ciPlotW} height={svgHeight} style={{ display: "block", overflow: "visible", margin: "0 auto" }}>
                {/* Zero / benchmark line */}
                <line x1={ciLabelW + ciScale(0)} x2={ciLabelW + ciScale(0)} y1={0} y2={svgHeight} stroke="#e0e0e0" strokeWidth={1} />
                {/* Selected predictor row */}
                {selected && (() => {
                    const cy = rowH / 2;
                    return (
                        <g>
                            <text x={ciLabelW - 6} y={cy} textAnchor="end" dominantBaseline="middle"
                                style={{ fontFamily: "DM Mono, sans-serif", fontSize: 11, fill: "#585858", fontWeight: 700 }}>
                                {PREDICTOR_NAMES[selected.predictor] || selected.predictor}
                            </text>
                            {ci.hasCI && selected.lo != null && (
                                <rect x={ciLabelW + ciScale(selected.lo)} y={cy - 4}
                                    width={Math.max(ciScale(selected.hi) - ciScale(selected.lo), 1)}
                                    height={8} rx={2} fill={color} opacity={0.3} />
                            )}
                            <circle cx={ciLabelW + ciScale(selected.value)} cy={cy} r={4} fill={color} />
                            <text x={ciLabelW + ciScale(selected.value) + 6} y={cy} dominantBaseline="middle"
                                style={{ fontFamily: "DM Mono, sans-serif", fontSize: 9, fill: color }}>
                                {selected.value > 0 ? "+" : ""}{selected.value.toFixed(1)}%
                            </text>
                        </g>
                    );
                })()}
                {/* Others + benchmark row */}
                {(() => {
                    const cy = (selected ? 1 : 0) * rowH + rowH / 2;
                    return (
                        <g>
                            <text x={ciLabelW - 6} y={cy} textAnchor="end" dominantBaseline="middle"
                                style={{ fontFamily: "DM Mono, sans-serif", fontSize: 11, fill: "#aaa", fontWeight: 700 }}>
                                Others
                            </text>
                            {/* Competitor dots */}
                            {ciCompetitors.map(e => (
                                <circle key={e.predictor} cx={ciLabelW + ciScale(e.value)} cy={cy} r={3} fill="#d0d0d0" />
                            ))}
                        </g>
                    );
                })()}
            </svg>
        );
    };

    // --- Subgroups (under/over predicted players) ---
    // Unlike overall/returns/combined, subgroups genuinely differ per gameweek,
    // so GW-by-GW mode reads the specific selected gameweek rather than falling
    // back to season.
    const subgroups = (isGwByGw
        ? predictorData?.by_gameweek?.[String(selectedGw)]?.subgroups
        : predictorData?.[timeframe]?.subgroups) || {};
    const isTeamMode = outlierMode === "teams";
    const underPredicted = (isTeamMode ? subgroups.under_predicted_teams : subgroups.under_predicted_players) || [];
    const overPredicted = (isTeamMode ? subgroups.over_predicted_teams : subgroups.over_predicted_players) || [];
    const positions = subgroups.positions || [];

    const handleImageError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://resources.premierleague.com/premierleague25/photos/players/110x140/placeholder.png";
    };

    const teamBadgeUrl = (teamCode) =>
        `https://omo.akamai.opta.net/image.php?secure=true&h=omo.akamai.opta.net&sport=football&entity=team&description=badges&dimensions=150&id=${teamCode}`;

    const renderOutlierList = (items, title, isUnder) => {
        if (!items.length) return <div className="detail-no-data">No data</div>;
        const top = items[0];
        return (
            <div className="detail-outlier-block">
                <div className="detail-section-title">{title}</div>
                {/* Top entry hero */}
                <div className="detail-outlier-hero">
                    {isTeamMode ? (
                        <img
                            src={teamBadgeUrl(top.team_code)}
                            height={47} width={47} alt={top.team_name}
                            className="detail-outlier-hero-img"
                        />
                    ) : (
                        <img
                            src={`https://resources.premierleague.com/premierleague25/photos/players/110x140/${top.code}.png`}
                            height={60} width={47} alt={top.web_name}
                            onError={handleImageError}
                            className="detail-outlier-hero-img"
                        />
                    )}
                    <div className="detail-outlier-hero-info">
                        <div className="detail-outlier-hero-name">{isTeamMode ? top.team_name : top.web_name}</div>
                        {!isTeamMode && <div className="detail-outlier-hero-team">{top.team_name}</div>}
                        <div className={`detail-outlier-hero-gap ${isUnder ? "under" : "over"}`}>
                            {top.error > 0 ? "+" : ""}{top.error.toFixed(2)} error
                        </div>
                    </div>
                </div>
                {/* Table header */}
                <div className="detail-outlier-row detail-outlier-header">
                    <div className="detail-outlier-cell badge"></div>
                    <div className="detail-outlier-cell name">{isTeamMode ? "Team" : "Player"}</div>
                    <div className="detail-outlier-cell num">Avg xPts</div>
                    <div className="detail-outlier-cell num">Avg Others</div>
                    <div className="detail-outlier-cell num">Avg Actual</div>
                    <div className="detail-outlier-cell num">Avg Error</div>
                </div>
                {/* Rows */}
                {items.map((p, i) => (
                    <div key={isTeamMode ? p.team_code : p.id} className={`detail-outlier-row ${i === 0 ? "first" : ""}`}>
                        <div className="detail-outlier-cell badge">
                            <img
                                src={teamBadgeUrl(p.team_code)}
                                height={14} width={14} alt={p.team_name}
                                style={{ verticalAlign: "middle" }}
                            />
                        </div>
                        <div className="detail-outlier-cell name">{isTeamMode ? p.team_name : p.web_name}</div>
                        <div className="detail-outlier-cell num">{p.avg_prediction.toFixed(1)}</div>
                        <div className="detail-outlier-cell num">{p.avg_prediction_others.toFixed(1)}</div>
                        <div className="detail-outlier-cell num">{p.avg_actual.toFixed(1)}</div>
                        <div className={`detail-outlier-cell num ${p.error < 0 ? "negative" : "positive"}`}>
                            {p.error > 0 ? "+" : ""}{p.error.toFixed(2)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // --- Positions breakdown chart ---
    const renderPositions = () => {
        if (!positions.length) return <div className="detail-no-data">No position data</div>;
        const { posValKey, posLoKey, posHiKey, posOthersKey } = activeMetric;
        const posLabelW = 36;
        const posPadR = 12;
        const posPlotW = Math.max(effectiveChartWidth - posLabelW - posPadR, 10);

        // Shared scale across all positions for the active metric, always including 0
        const allPosVals = positions.flatMap(p => {
            const vals = [p[posLoKey], p[posHiKey], p[posValKey]].filter(v => v != null);
            if (p.others) p.others.forEach(o => { if (o[posOthersKey] != null) vals.push(o[posOthersKey]); });
            return vals;
        });
        const posMin = Math.min(0, d3.min(allPosVals) ?? 0) - 1;
        const posMax = Math.max(0, d3.max(allPosVals) ?? 0) + 1;
        const posScale = d3.scaleLinear().domain([posMin, posMax]).range([0, posPlotW]);

        const rowH = 28;
        const svgHeight = positions.length * rowH + 8;

        return (
            <svg width={posLabelW + posPlotW + posPadR} height={svgHeight} style={{ display: "block", overflow: "visible", margin: "0 auto" }}>
                {/* Zero line */}
                <line x1={posLabelW + posScale(0)} x2={posLabelW + posScale(0)} y1={0} y2={svgHeight} stroke="#e0e0e0" strokeWidth={1} />
                {positions.map((pos, i) => {
                    const cy = i * rowH + rowH / 2;
                    const val = pos[posValKey];
                    const lo = pos[posLoKey];
                    const hi = pos[posHiKey];
                    const hasCI = lo != null && hi != null;
                    if (val == null) return null;
                    return (
                        <g key={pos.position_name}>
                            {/* Position label */}
                            <text x={posLabelW - 6} y={cy} textAnchor="end" dominantBaseline="middle"
                                style={{ fontFamily: "DM Mono, sans-serif", fontSize: 11, fill: "#585858", fontWeight: 700 }}>
                                {pos.position_name}
                            </text>
                            {/* CI bar */}
                            {hasCI && (
                                <rect x={posLabelW + posScale(lo)} y={cy - 5}
                                    width={Math.max(posScale(hi) - posScale(lo), 1)}
                                    height={10} rx={3} fill={color} opacity={0.2} />
                            )}
                            {/* Competitor dots */}
                            {(pos.others || []).map(o => {
                                const ov = o[posOthersKey];
                                return ov != null ? (
                                    <circle key={o.predictor} cx={posLabelW + posScale(ov)} cy={cy} r={3} fill="#d0d0d0" />
                                ) : null;
                            })}
                            {/* Selected predictor dot */}
                            <circle cx={posLabelW + posScale(val)} cy={cy} r={4.5} fill={color} />
                            {/* Value label */}
                            <text x={posLabelW + posScale(val) + 7} y={cy} dominantBaseline="middle"
                                style={{ fontFamily: "DM Mono, sans-serif", fontSize: 9, fill: color }}>
                                {val > 0 ? "+" : ""}{val.toFixed(1)}%
                            </text>
                        </g>
                    );
                })}
            </svg>
        );
    };

    return (
        <>
            {/* Backdrop — mobile only via CSS */}
            <div className={`detail-backdrop ${closing ? "closing" : ""}`} onClick={handleClose} />

            <div className={`detail-panel ${closing ? "closing" : ""} ${isGwByGw ? "detail-panel-narrow" : ""}`}>
                {/* Header */}
                <div className="detail-header">
                    <div className="detail-header-name">
                        <div className="detail-header-swatch" style={{ background: color }} />
                        {displayName}
                    </div>
                    <button className="detail-close" onClick={handleClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Scrollable content — two columns on desktop */}
                <div className="detail-content-wrapper" ref={contentRef}>
                    {/* Left column: charts — not shown in GW-by-GW mode, which only
                        surfaces the under/over-predicted players for that gameweek */}
                    {!isGwByGw && <div className="detail-content-left">

                        {/* Shared metric tabs */}
                        <div className="detail-metric-tabs">
                            {DETAIL_METRICS.map((m, i) => (
                                <React.Fragment key={m.key}>
                                    {i === 1 && <span className="detail-metric-tab-separator" />}
                                    <button
                                        className={`accuracy-chart-metric-tab ${m.key === "combined" ? "primary" : ""} ${metric === m.key ? "active" : "inactive"}`}
                                        onClick={() => setMetric(m.key)}>
                                        {m.label}
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>

                        {/* CI section */}
                        <div className="detail-section">
                            <div className="detail-section-title">Confidence Intervals</div>
                            <div className="detail-section-subtitle">% improvement vs benchmark with 95% CI</div>
                            {(() => {
                                const ci = ciData.find(c => c.key === metric);
                                return ci ? renderCI(ci) : null;
                            })()}
                        </div>

                        {/* GW performance chart */}
                        <div className="detail-section">
                            <div className="detail-section-title">Gameweek Performance</div>
                            {allSeries.length === 0 ? (
                                <div className="detail-no-data">No gameweek data yet</div>
                            ) : (
                                <svg width={effectiveChartWidth} height={svgH} style={{ display: "block", overflow: "visible", margin: "0 auto" }}>
                                    <g transform={`translate(${margin.left},${margin.top})`}>
                                        {yTicks.map(t => (
                                            <line key={t} x1={0} x2={w} y1={yScale(t)} y2={yScale(t)}
                                                stroke="#f0f0f0" strokeDasharray="3 3" />
                                        ))}
                                        <line x1={0} x2={w} y1={yScale(0)} y2={yScale(0)} stroke="#ddd" />
                                        {/* Competitors */}
                                        {competitors.map(s => (
                                            <path key={s.predictor} d={lineFn(s.values)}
                                                fill="none" stroke="#e0e0e0" strokeWidth={1.5} strokeLinejoin="round" />
                                        ))}
                                        {/* Selected */}
                                        {selectedSeries && <>
                                            <path d={lineFn(selectedSeries.values)}
                                                fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" />
                                            {selectedSeries.values.map(v => (
                                                <circle key={v.gw} cx={xScale(v.gw)} cy={yScale(v.value)} r={3.5} fill={color} />
                                            ))}
                                        </>}
                                        {/* X axis */}
                                        <g transform={`translate(0,${h})`}>
                                            <line x1={0} x2={w} stroke="#ddd" />
                                            {xTicks.map(t => (
                                                <text key={t} x={xScale(t)} y={16} textAnchor="middle"
                                                    style={{ fontFamily: "DM Mono, sans-serif", fontSize: 9, fill: "#999" }}>
                                                    GW{t}
                                                </text>
                                            ))}
                                        </g>
                                        {/* Y axis */}
                                        <g>
                                            <line y1={0} y2={h} stroke="#ddd" />
                                            {yTicks.map(t => (
                                                <text key={t} x={-5} y={yScale(t)} textAnchor="end" dominantBaseline="middle"
                                                    style={{ fontFamily: "DM Mono, sans-serif", fontSize: 9, fill: "#999" }}>
                                                    {t}%
                                                </text>
                                            ))}
                                        </g>
                                    </g>
                                </svg>
                            )}
                        </div>

                        {/* Positions breakdown */}
                        {positions.length > 0 && (
                            <>
                                <hr className="detail-divider" />
                                <div className="detail-section">
                                    <div className="detail-section-title">Performance by Position</div>
                                    <div className="detail-section-subtitle">% improvement vs benchmark</div>
                                    {renderPositions()}
                                </div>
                            </>
                        )}
                    </div>}

                    {/* Right column: outliers — full width in GW-by-GW mode */}
                    <div className={`detail-content-right ${isGwByGw ? "detail-content-right-full" : ""}`}>
                        <div className="detail-outlier-mode-row">
                            <label htmlFor="detail-outlier-mode">Show</label>
                            <select
                                id="detail-outlier-mode"
                                value={outlierMode}
                                onChange={(e) => setOutlierMode(e.target.value)}
                            >
                                <option value="players">Players</option>
                                <option value="teams">Teams</option>
                            </select>
                        </div>
                        {renderOutlierList(underPredicted, "Most Under-Predicted", true)}
                        {renderOutlierList(overPredicted, "Most Over-Predicted", false)}
                    </div>
                </div>
            </div>
        </>
    );
};

export default PredictorDetailPanel;







